import React, {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type SocialWidgetProps,
  type SocialThemingProps,
  SOCIAL_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionSocial for imperative control.
 */
export interface LegionSocialHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildSocialWidgetUrl(props: SocialWidgetProps): string {
  const {
    baseUrl,
    layout = "feed",
    theme = "light",
    rows = 3,
    cols = 3,
    communityUrl,
    channel,
    height = "800",
  } = props;

  const url = new URL("/widget/social", baseUrl);

  if (layout) url.searchParams.set("layout", layout);
  if (theme) url.searchParams.set("theme", theme);

  if (layout === "grid") {
    url.searchParams.set("rows", String(rows));
    url.searchParams.set("cols", String(cols));
    if (communityUrl) {
      url.searchParams.set("communityUrl", communityUrl);
    }
  } else if (layout === "feed") {
    // For feed layout, pass height to enable constrained scrolling
    const rawHeight = String(height).replace("px", "");
    if (rawHeight !== "800") {
      url.searchParams.set("height", rawHeight);
    }
  }

  if (channel) {
    url.searchParams.set("channel", channel);
  }

  // Append theming params
  const themingKeys = Object.keys(SOCIAL_THEMING_PROP_TO_PARAM) as Array<
    keyof SocialThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(SOCIAL_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionSocial – a React component that embeds the Legion Social widget
 * via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionSocial } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionSocial
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       layout="feed"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionSocial = forwardRef<LegionSocialHandle, SocialWidgetProps>(
  function LegionSocial(props, ref) {
    const {
      theme = "light",
      layout = "feed",
      authToken,
      rows,
      cols,
      communityUrl,
      channel,
      width = "600px",
      height = "800px",
      className,
      style,
      onLoad,
      onError,
      baseUrl,
      ...rest
    } = props;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.location.reload();
          }
        },
      }),
      []
    );

    const src = useMemo(
      () => buildSocialWidgetUrl(props),
      [
        baseUrl,
        layout,
        theme,
        rows,
        cols,
        communityUrl,
        channel,
        height,
        ...Object.keys(SOCIAL_THEMING_PROP_TO_PARAM).map(
          (k) => rest[k as keyof typeof rest]
        ),
      ]
    );

    const handleLoad = useCallback(() => {
      // Auth only for non-grid layouts
      if (layout !== "grid" && authToken && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: authToken },
          baseUrl
        );
      }
      onLoad?.({ widget: "social", layout, theme });
    }, [authToken, baseUrl, layout, theme, onLoad]);

    const handleError = useCallback(() => {
      onError?.("Failed to load social widget");
    }, [onError]);

    const containerStyle: React.CSSProperties = {
      display: "block",
      width,
      height,
      ...style,
    };

    const iframeStyle: React.CSSProperties = {
      border: "none",
      borderRadius: "8px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      width: "100%",
      height: "100%",
    };

    return (
      <div className={className} style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={src}
          title="Social Feed Widget"
          width="100%"
          height="100%"
          loading="lazy"
          style={iframeStyle}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }
);

